import { useFeature } from '@optimizely/react-sdk';
import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	type InfoConfig,
	type InfoNamespace,
	InfoProvider as SharedInfoProvider,
} from '../components/Info/InfoProvider';
import Link from '../components/Link/Link';
import { useSettings } from '../hooks/settings/use-settings.hook';
import { useLegacyWorkspace } from '../hooks/workspace/use-legacy-workspace';

type InfoUser = InfoConfig['user'];

// Router-aware: navigates in place and stops the click reaching a table row.
const InfoLink: FC<{ href: string; children?: ReactNode }> = ({ href, children }) => (
	<Link inheritSize url={href}>
		{children}
	</Link>
);

// Module-level so the provider value does not change identity on every render.
const INFO_COMPONENTS = { Link: InfoLink };

// Module-level: runs as a hook inside each Info type, so it needs a stable identity. Each namespace
// is its own lazy chunk, loaded when that Info type first renders. It must not suspend: the Gantt
// draws these inside FullCalendar portals, and a suspend hides the page-level tree, including the
// popover triggers, in a loop that ends in "Maximum update depth exceeded". The hook re-renders
// when the namespace arrives.
const useInfoTranslate = (ns: InfoNamespace) => useTranslation(ns, { useSuspense: false }).t;

export const InfoProvider: FC<PropsWithChildren> = ({ children }) => {
	const { currency } = useSettings();
	const workspace = useLegacyWorkspace();
	const [hideItemStatusText] = useFeature('hide_item_status_text');

	const config = useMemo<InfoConfig>(() => {
		const legacyUser = window.global.central.user();

		return {
			user: { userRole: { permissions: legacyUser.userRole.permissions } } as unknown as InfoUser,
			workspace,
			useTranslate: useInfoTranslate,
			currency,
			locale: navigator.language,
			featureFlags: { hide_item_status_text: hideItemStatusText },
			components: INFO_COMPONENTS,
		};
	}, [currency, workspace, hideItemStatusText]);

	return <SharedInfoProvider {...config}>{children}</SharedInfoProvider>;
};
